import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { ApiError } from '../api/errors'
import {
  loginPassword,
  loginSecondFactor,
  normalizeCode,
  resolveTenantId,
  type LoginTicket,
  type SessionUser,
} from '../api/auth'
import { t } from '../i18n'
import { FirstAccessFlow } from './FirstAccessFlow'
import { brand, gradients } from '../theme'

/**
 * Painel de marca = fundo em degrade navy -> asset NEGATIVO. Variante SEM o
 * retangulo de fundo do SVG original (o retangulo navy chapado aparecia como uma
 * caixa sobre o degrade). Geometria e cores do logo intactas (Manual v4.0 sec. 10).
 */
export const LOGIN_LOGO_SRC = '/brand/strongerh-one-horizontal-chapada-negativa-transparente.svg'
/** No celular o painel some: o logo POSITIVO chapado vai acima do formulario, sobre branco. */
export const LOGIN_LOGO_MOBILE_SRC = '/brand/strongerh-one-horizontal-chapada.svg'

/**
 * Assinatura da GENTE no rodape do painel (S32-8..S32-11, aplicadas em 2026-09-28):
 * "uma solucao" + logo horizontal NEGATIVO do kit oficial (painel em degrade navy ->
 * asset negativo, o mesmo par da marca do produto). O arquivo e copia IDENTICA de
 * `KIT_LOGO_GENTE/SISTEMAS/logos/logo-horizontal-negativo.svg`, fora de `/brand`
 * (que e da marca STRONGERH ONE) e nunca editado. Altura 32 px -> largura ~136 px
 * pelo viewBox 1474x348, acima do minimo de 120 px do kit (a S32-11 fixava 22 px,
 * que dava 93 px e violava o kit). SEM opacidade: o manual da marca GENTE proibe
 * alterar as cores do logo (decisao de Marcelo, 2026-09-28; a proposta de 0,8 caiu).
 * No celular o painel some e nao ha rodape: nada muda ali.
 */
export const LOGIN_GENTE_LOGO_SRC = '/gente/logo-horizontal-negativo.svg'
export const LOGIN_GENTE_LOGO_HEIGHT = 32
/** Proporcao largura/altura do viewBox do kit (1474 x 348). */
export const GENTE_LOGO_ASPECT = 1474 / 348
/** Largura minima de reproducao exigida pelo kit, em px. */
export const GENTE_LOGO_MIN_WIDTH = 120

/**
 * Tela-piloto de login (S25-10; recorte S27-6, tela 1 de 6).
 *
 * Fluxo de DOIS passos do ADR-0030 D11 / ADR-0031:
 *   1. e-mail + senha  -> ticket + `next_step`
 *   2. codigo (8 digitos por e-mail, 6 do autenticador, ou recuperacao) -> sessao
 * `password_change`, `mfa_enroll` e `mfa_email_verify` abrem a tela de PRIMEIRO
 * ACESSO (tela 2 do recorte, S32) -- `FirstAccessFlow`, no mesmo componente.
 * A senha digitada aqui fica em memoria (useRef) SO nesse caso, porque o
 * `/password/change` a exige (S32-3), e e esquecida logo apos a troca.
 *
 * LAYOUT (S31-7, 2026-09-24): tela cheia dividida, no padrao de login do
 * Rippling -- painel de marca a esquerda (degrade navy da paleta oficial + a
 * "viga" laranja como acento grafico) e formulario limpo a direita. No celular
 * o painel some e fica so o formulario.
 *
 * LGPD (ADR-0024 D10.1): nenhuma rota carrega e-mail; o estado vive em memoria.
 * Nada e gravado em localStorage/sessionStorage.
 */

type Step =
  | { kind: 'credentials' }
  | { kind: 'code'; ticket: LoginTicket }
  | { kind: 'firstAccess'; ticket: LoginTicket }
  | { kind: 'done'; user: SessionUser }

type Phase = 'credentials' | 'code'

function loginErrorMessage(error: unknown, phase: Phase): string {
  if (!(error instanceof ApiError)) return t.error.genericMessage
  if (error.isNetworkError) return t.error.networkMessage
  if (error.status === 503) return t.login.errors.mfaUnavailable
  if (error.isUnauthorized) {
    return phase === 'credentials'
      ? t.login.errors.invalidCredentials
      : t.login.errors.invalidCode
  }
  return t.error.genericMessage
}

function ErrorAlert({ error, phase }: { error: unknown; phase: Phase }) {
  const requestId =
    error instanceof ApiError && !error.isUnauthorized ? error.requestId : null
  return (
    <Alert severity="error" role="alert">
      <Typography variant="body2">{loginErrorMessage(error, phase)}</Typography>
      {requestId && (
        <Typography variant="caption" component="code" sx={{ fontFamily: 'monospace' }}>
          {t.error.requestIdHint} {requestId}
        </Typography>
      )}
    </Alert>
  )
}

export function LoginPage({
  tenantId = resolveTenantId(),
  onSignedIn,
}: {
  tenantId?: string
  /** B1 (s35): a rota real leva para dentro do app; os testes isolados nao passam nada. */
  onSignedIn?: (user: SessionUser) => void
}) {
  const [step, setStep] = useState<Step>({ kind: 'credentials' })
  useEffect(() => {
    if (step.kind === 'done' && onSignedIn) onSignedIn(step.user)
  }, [step, onSignedIn])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  // S32-3: a senha provisoria so sobrevive ao passo 1 no caminho do primeiro
  // acesso, e so em memoria. Nunca estado de React (nao vai a DevTools/efeitos).
  const provisionalPassword = useRef('')

  // A130 (s32, achado pelo teste P5 da tela 2): o MutationCache do TanStack guarda
  // as VARIAVEIS da mutacao -- aqui, e-mail E SENHA -- por 5 min (gcTime padrao)
  // depois do login. `gcTime: 0` + `reset()` no sucesso tiram a senha de la.
  const credentials = useMutation({
    mutationFn: loginPassword,
    gcTime: 0,
    onSuccess: (ticket, input) => {
      setPassword('')
      credentials.reset()
      if (ticket.nextStep === 'mfa_email' || ticket.nextStep === 'mfa_totp') {
        setStep({ kind: 'code', ticket })
      } else {
        provisionalPassword.current = ticket.nextStep === 'password_change' ? input.password : ''
        setStep({ kind: 'firstAccess', ticket })
      }
    },
  })

  const secondFactor = useMutation({
    mutationFn: loginSecondFactor,
    gcTime: 0,
    onSuccess: (user) => {
      setCode('')
      secondFactor.reset()
      setStep({ kind: 'done', user })
    },
  })

  function backToStart() {
    provisionalPassword.current = ''
    credentials.reset()
    secondFactor.reset()
    setCode('')
    setStep({ kind: 'credentials' })
  }

  function submitCredentials(e: FormEvent) {
    e.preventDefault()
    credentials.mutate({ tenantId, username: email.trim(), password })
  }

  function submitCode(e: FormEvent) {
    e.preventDefault()
    if (step.kind !== 'code') return
    secondFactor.mutate({ ticket: step.ticket.ticket, code: normalizeCode(code) })
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
      }}
    >
      <Box
        component="aside"
        data-testid="login-brand-panel"
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          background: gradients.brandPanel,
        }}
      >
        <Box
          component="img"
          src={LOGIN_LOGO_SRC}
          alt={t.app.name}
          sx={{ height: 40, width: 'auto', alignSelf: 'flex-start' }}
        />
        <Stack spacing={2} sx={{ maxWidth: 440 }}>
          <Box sx={{ width: 72, height: 6, borderRadius: 1, background: gradients.accent }} />
          <Typography variant="h1" component="p" sx={{ color: brand.white }}>
            {t.login.brandHeadline}
          </Typography>
          <Typography variant="body1" sx={{ color: brand.grey }}>
            {t.login.brandSubline}
          </Typography>
        </Stack>
        <Stack spacing={1} alignItems="flex-start">
          <Typography variant="caption" sx={{ color: brand.grey }}>
            {t.login.brandSignature}
          </Typography>
          <Box
            component="img"
            src={LOGIN_GENTE_LOGO_SRC}
            alt={t.login.brandSignatureAlt}
            sx={{ height: LOGIN_GENTE_LOGO_HEIGHT, width: 'auto', display: 'block' }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 6 },
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Box
            component="img"
            src={LOGIN_LOGO_MOBILE_SRC}
            alt={t.app.name}
            sx={{ display: { xs: 'block', md: 'none' }, height: 32, width: 'auto', mb: 4 }}
          />
            {step.kind === 'credentials' && (
              <Box component="form" onSubmit={submitCredentials} noValidate>
                <Stack spacing={2}>
                  <Typography variant="h2" component="h1">
                    {t.login.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.login.subtitle}
                  </Typography>
                    {credentials.isError && (
                    <ErrorAlert error={credentials.error} phase="credentials" />
                  )}
                  <TextField
                    label={t.login.emailLabel}
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                  />
                  <TextField
                    label={t.login.passwordLabel}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!email.trim() || !password || credentials.isPending}
                  >
                    {credentials.isPending ? t.common.loading : t.login.submit}
                  </Button>
                </Stack>
              </Box>
            )}

            {step.kind === 'code' && (
              <Box component="form" onSubmit={submitCode} noValidate>
                <Stack spacing={2}>
                  <Typography variant="h2" component="h1">
                    {t.login.codeTitle}
                  </Typography>
                  <Typography variant="body2">
                    {step.ticket.nextStep === 'mfa_email'
                      ? t.login.codeHintEmail
                      : t.login.codeHintTotp}
                  </Typography>
                  {secondFactor.isError && <ErrorAlert error={secondFactor.error} phase="code" />}
                  <TextField
                    label={t.login.codeLabel}
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 16 } }}
                    helperText={t.login.codeRecoveryHint}
                    required
                    fullWidth
                    autoFocus
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={normalizeCode(code).length < 6 || secondFactor.isPending}
                  >
                    {secondFactor.isPending ? t.common.loading : t.login.codeSubmit}
                  </Button>
                  <Button variant="text" onClick={backToStart}>
                    {t.login.back}
                  </Button>
                </Stack>
              </Box>
            )}

            {step.kind === 'firstAccess' && (
              <FirstAccessFlow
                ticket={step.ticket}
                currentPassword={provisionalPassword.current}
                onPasswordConsumed={() => {
                  provisionalPassword.current = ''
                }}
                onRestart={backToStart}
                onSignedIn={(user) => {
                  provisionalPassword.current = ''
                  setStep({ kind: 'done', user })
                }}
              />
            )}

            {step.kind === 'done' && (
              <Stack spacing={2}>
                <Typography variant="h2" component="h1">
                  {t.login.successTitle}
                </Typography>
                <Alert severity="success">
                  {t.login.successMessage} {step.user.username}
                </Alert>
              </Stack>
            )}
        
        </Box>
      </Box>
    </Box>
  )
}
